import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Star, Truck, Shield, Coffee, Leaf } from "lucide-react";
import api from "../utils/api";
import { useCart } from "../context/CartContext";
import "./HomePage.css";

const HERO_IMG =
  "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=1600&q=80";

const categories = [
  { name: "Espresso", icon: "☕", path: "/shop/espresso", color: "#1a4731" },
  { name: "Filter", icon: "☕", path: "/shop/filter", color: "#2d6a4f" },
  { name: "Cold Brew", icon: "🧊", path: "/shop/cold-brew", color: "#1b4332" },
  { name: "Capsules", icon: "🔘", path: "/shop/capsule", color: "#40916c" },
  {
    name: "Accessories",
    icon: "⚙️",
    path: "/shop/accessories",
    color: "#52b788",
  },
  { name: "Gifts", icon: "🎁", path: "/shop/gift-sets", color: "#74c69d" },
];

export default function HomePage() {
  const [featured, setFeatured] = useState([]);
  const { addItem } = useCart();

  useEffect(() => {
    api
      .get("/products/featured")
      .then((res) => setFeatured(res.data.products))
      .catch(console.error);
  }, []);

  return (
    <div className="home">
      {/* Hero */}
      <section className="hero" style={{ backgroundImage: `url(${HERO_IMG})` }}>
        <div className="hero-overlay" />
        <div className="hero-content container">
          <span className="hero-tag">Swiss Premium Coffee</span>
          <h1>
            Taste the World,
            <br />
            <span>One Cup at a Time</span>
          </h1>
          <p>
            Sourced from the finest coffee-growing regions. Roasted in
            Switzerland. Delivered to your door.
          </p>
          <div className="hero-actions">
            <Link to="/shop" className="btn-gold">
              Shop Now <ArrowRight size={18} />
            </Link>
            <Link
              to="/shop/espresso"
              className="btn-secondary"
              style={{ borderColor: "rgba(255,255,255,0.5)", color: "#fff" }}
            >
              Explore Espresso
            </Link>
          </div>
          <div className="hero-stats">
            <div>
              <span>50+</span>
              <p>Origin Countries</p>
            </div>
            <div>
              <span>10K+</span>
              <p>Happy Customers</p>
            </div>
            <div>
              <span>4.9★</span>
              <p>Average Rating</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="features-bar">
        <div className="container features-grid">
          <div className="feature">
            <Truck size={24} />
            <div>
              <strong>Free Shipping</strong>
              <p>On orders over CHF 50</p>
            </div>
          </div>
          <div className="feature">
            <Shield size={24} />
            <div>
              <strong>Quality Guarantee</strong>
              <p>100% satisfaction or refund</p>
            </div>
          </div>
          <div className="feature">
            <Leaf size={24} />
            <div>
              <strong>Sustainably Sourced</strong>
              <p>Direct trade & organic</p>
            </div>
          </div>
          <div className="feature">
            <Coffee size={24} />
            <div>
              <strong>Expert Roasters</strong>
              <p>Roasted fresh weekly</p>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="section categories-section container">
        <div className="section-header">
          <h2>Shop by Category</h2>
          <Link to="/shop" className="see-all">
            View all <ArrowRight size={16} />
          </Link>
        </div>
        <div className="categories-grid">
          {categories.map((cat) => (
            <Link key={cat.path} to={cat.path} className="category-card">
              <span className="category-icon">{cat.icon}</span>
              <span className="category-name">{cat.name}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured Products */}
      {featured.length > 0 && (
        <section className="section container">
          <div className="section-header">
            <h2>Featured Products</h2>
            <Link to="/shop" className="see-all">
              View all <ArrowRight size={16} />
            </Link>
          </div>
          <div className="products-grid">
            {featured.map((product) => (
              <ProductCard
                key={product._id}
                product={product}
                addItem={addItem}
              />
            ))}
          </div>
        </section>
      )}

      {/* Banner */}
      <section className="promo-banner container">
        <div className="promo-content">
          <h2>Swiss Coffee Subscription</h2>
          <p>
            Get freshly roasted coffee delivered monthly. Customize your roast,
            grind size, and quantity. Cancel anytime.
          </p>
          <Link to="/shop" className="btn-gold">
            Start Subscription <ArrowRight size={18} />
          </Link>
        </div>
        <div className="promo-image">
          <img
            src="https://images.unsplash.com/photo-1511920170033-f8396924c348?w=600"
            alt="Coffee subscription"
          />
        </div>
      </section>

      {/* Testimonials */}
      <section className="section testimonials container">
        <div className="section-header">
          <h2>What Our Customers Say</h2>
        </div>
        <div className="testimonials-grid">
          {[
            {
              name: "Sophie M.",
              city: "Zürich",
              text: "The Ethiopian Yirgacheffe is outstanding. Complex, fruity, and absolutely delicious.",
              stars: 5,
            },
            {
              name: "Marco F.",
              city: "Geneva",
              text: "Best espresso blend I've found in Switzerland. The crema is perfect every time.",
              stars: 5,
            },
            {
              name: "Anna K.",
              city: "Basel",
              text: "Fast delivery, beautiful packaging, and the coffee is always fresh. 10/10.",
              stars: 5,
            },
          ].map((t, i) => (
            <div key={i} className="testimonial">
              <div className="stars">{Array(t.stars).fill("★").join("")}</div>
              <p>"{t.text}"</p>
              <strong>
                {t.name} — {t.city}
              </strong>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function ProductCard({ product, addItem }) {
  return (
    <div className="product-card">
      <Link to={`/product/${product._id}`}>
        <div className="product-img-wrap">
          <img
            src={
              product.thumbnail ||
              "https://images.unsplash.com/photo-1447933601403-0c6688de566e?w=400"
            }
            alt={product.name}
          />
          {product.isFeatured && (
            <span className="badge product-badge">Featured</span>
          )}
        </div>
      </Link>
      <div className="product-body">
        <div className="product-category">{product.category}</div>
        <Link to={`/product/${product._id}`}>
          <h3>{product.name}</h3>
        </Link>
        <p>{product.shortDescription}</p>
        {product.rating > 0 && (
          <div className="product-rating">
            <span className="stars">
              {"★".repeat(Math.round(product.rating))}
            </span>
            <span>({product.numReviews})</span>
          </div>
        )}
        <div className="product-footer">
          <span className="product-price">CHF {product.price.toFixed(2)}</span>
          <button
            className="btn-primary add-to-cart"
            onClick={() => addItem(product, 1)}
            disabled={product.stock === 0}
          >
            {product.stock === 0 ? "Out of Stock" : "Add to Cart"}
          </button>
        </div>
      </div>
    </div>
  );
}
